import { createReducer, on, Action } from '@ngrx/store';
import * as actions from './wizzard.actions';

const initialState: OLO.State.Wizzard = {
    errors: [],
    recalculatePrices: {
        isCalculating: false,
        hasSucceeded: false,
        hasFailed: false,
    },
    itemsMenuFlow: null,
    itemsSimple: null,
};

function createNewIngredient(ingredient: APICommon.ProductIngredientExtended): OLO.State.Wizzard.IngredientAdditionModelExtended {
    return {
        ID: ingredient.Id,
        OrderItemId: ingredient.IngredientProductID,
        IngredientPLU: ingredient.IngredientPLU,
        IngredientName: ingredient.IngredientName,
        ExtraPrice: ingredient.ExtraPrice,
        Qty: 1,
        _IngredientDisplayGroupId: ingredient.IngredientDisplayGroupId,
    };
}

export const wizzardReducerFn = createReducer(
    initialState,
    on(actions.WizzardStepMenuFlowInit, (state) => ({
        ...state,
        errors: [],
        recalculatePrices: {
            isCalculating: false,
            hasSucceeded: false,
            hasFailed: false,
        },
        itemsSimple: null,
        itemsMenuFlow: null,
    })),
    on(actions.WizzardStepMenuFlowMount, (state, action) => ({
        ...state,
        recalculatePrices: {
            isCalculating: false,
            hasSucceeded: false,
            hasFailed: false,
        },
        itemsMenuFlow: {
            ...action.item,
        },
    })),
    on(actions.WizzardValidate, (state, action) => ({
        ...state,
        errors: action.errors,
    })),
    on(actions.WizzardUnmountAll, (state) => ({
        ...state,
        recalculatePrices: {
            isCalculating: false,
            hasSucceeded: false,
            hasFailed: false,
        },
        itemsSimple: null,
        itemsMenuFlow: null,
        errors: [],
    })),
    on(actions.WizzardStepSimpleItemMount, (state, action) => ({
        ...state,
        recalculatePrices: {
            isCalculating: false,
            hasSucceeded: false,
            hasFailed: false,
        },
        itemsSimple: { ...action.item },
    })),
    on(actions.WizzardMenuFlowUpsellSetupRequest, (state) => ({
        ...state,
        recalculatePrices: {
            isCalculating: false,
            hasSucceeded: false,
            hasFailed: false,
        },
    })),
    on(actions.WizzardMountMenuFlowUpsell, (state, action) => ({
        ...state,
        recalculatePrices: {
            isCalculating: false,
            hasSucceeded: false,
            hasFailed: false,
        },
        itemsMenuFlow: { ...action.item },
    })),
    on(actions.WizzardSimpleItemChangeQuantity, (state, action) => ({
        ...state,
        itemsSimple: {
            ...state.itemsSimple,
            Quantity: action.changeValue,
        },
    })),
    on(actions.WizzardSimpleItemDecrement, (state, action) => ({
        ...state,
        itemsSimple: {
            ...state.itemsSimple,
            Quantity: (state.itemsSimple?.Quantity ?? 0) - action.changeValue,
        },
    })),
    on(actions.WizzardSimpleItemIncrement, (state, action) => ({
        ...state,
        itemsSimple: {
            ...state.itemsSimple,
            Quantity: (state.itemsSimple?.Quantity ?? 0) + action.changeValue,
        },
    })),
    on(actions.WizzardAddOptionalIngredient, (state, action) => {
        const newIngredient = createNewIngredient(action.ingredient);
        const selectedIngredientsPLUs = state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs || [];

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple.IngredientsChanges,
                    IngredientsAdded: [...state.itemsSimple.IngredientsChanges.IngredientsAdded, newIngredient],
                    _SelectedIngredientPLUs: [...selectedIngredientsPLUs, newIngredient.IngredientPLU],
                },
            },
        };
    }),
    on(actions.WizzardRemoveOptionalIngredient, (state, action) => ({
        ...state,
        itemsSimple: {
            ...state.itemsSimple,
            IngredientsChanges: {
                ...state.itemsSimple?.IngredientsChanges,
                IngredientsAdded: state.itemsSimple.IngredientsChanges.IngredientsAdded.filter((ingredient) => ingredient.IngredientPLU !== action.ingredientPLU),
                _SelectedIngredientPLUs: state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs?.filter((plu) => plu !== action.ingredientPLU),
            },
        },
    })),
    on(actions.WizzardIncrementOptionalIngredient, (state, action) => {
        const updatedIngredientsAdded = state.itemsSimple.IngredientsChanges.IngredientsAdded.map((ingredient) => {
            const found = ingredient.IngredientPLU === action.ingredientPLU;

            const basePrice = ingredient.ExtraPrice / ingredient.Qty;
            const nextQty = ingredient.Qty + 1;
            const updatedIngredient = { ...ingredient, Qty: nextQty, ExtraPrice: basePrice * nextQty };

            return found ? updatedIngredient : ingredient;
        });

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple?.IngredientsChanges,
                    IngredientsAdded: updatedIngredientsAdded,
                },
            },
        };
    }),
    on(actions.WizzardDecrementOptionalIngredient, (state, action) => {
        const updatedIngredientsAdded = state.itemsSimple.IngredientsChanges.IngredientsAdded.map((ingredient) => {
            const found = ingredient.IngredientPLU === action.ingredientPLU;

            const basePrice = ingredient.ExtraPrice / ingredient.Qty;
            const nextQty = ingredient.Qty - 1;
            const updatedIngredient = { ...ingredient, Qty: nextQty, ExtraPrice: basePrice * nextQty };

            return found ? updatedIngredient : ingredient;
        });

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple.IngredientsChanges,
                    IngredientsAdded: updatedIngredientsAdded,
                },
            },
        };
    }),
    on(actions.WizzardReplaceOptionalIngredient, (state, action) => {
        const ingredientGroupId = action.newIngredient.IngredientDisplayGroupId;
        const ingredientToAdd = createNewIngredient(action.newIngredient);
        const ingredientToRemove = state.itemsSimple.IngredientsChanges.IngredientsAdded.find((ing) => ing._IngredientDisplayGroupId === ingredientGroupId);

        const updatedIngredientsAdded = state.itemsSimple.IngredientsChanges.IngredientsAdded.filter((ing) => ing._IngredientDisplayGroupId !== ingredientGroupId).concat(
            ingredientToAdd,
        );

        const updatedSelectedIngredientPLUs = state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs
            ?.filter((plu) => (ingredientToRemove ? plu !== ingredientToRemove.IngredientPLU : true))
            ?.concat(action.newIngredient.IngredientPLU);

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple?.IngredientsChanges,
                    IngredientsAdded: updatedIngredientsAdded,
                    _SelectedIngredientPLUs: updatedSelectedIngredientPLUs,
                },
            },
        };
    }),
    on(actions.WizzardSelectModifierForOptionalIngredient, (state, action) => {
        const updatedIngredientsAdded = state.itemsSimple.IngredientsChanges.IngredientsAdded.map((ingredient) => {
            const foundIngredient = action.modifier.IngredientPLU === ingredient.IngredientPLU;

            if (foundIngredient) {
                const modifierAlreadyAdded = ingredient.ModifierID === action.modifier.ModifierID;

                if (modifierAlreadyAdded) {
                    return {
                        ...ingredient,
                        ExtraPrice: action.originalExtraPrice * ingredient.Qty,
                        ModifierID: undefined,
                        ModifierName: undefined,
                    };
                }

                return {
                    ...ingredient,
                    ExtraPrice: action.modifier.ExtraPrice * ingredient.Qty,
                    ModifierID: action.modifier.ModifierID,
                    ModifierName: action.modifier.ModifierName,
                };
            }

            return ingredient;
        });

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple?.IngredientsChanges,
                    IngredientsAdded: updatedIngredientsAdded,
                },
            },
        };
    }),
    on(actions.WizzardRestoreIngredient, (state, action) => {
        const swapIds = action.ingredient.Swaps?.map((swap) => swap.NewPLU) || [];

        const updatedIngredientsRemoved = state.itemsSimple.IngredientsChanges.IngredientsRemoved.filter((ing) => ing.IngredientPLU !== action.ingredient.IngredientPLU);

        const updatedIngredientsSwapped = state.itemsSimple.IngredientsChanges.IngredientsSwapped.filter(
            (ingredient) => ingredient.OldIngredientPLU !== action.ingredient.IngredientPLU,
        );

        const updatedSelectedIngredientPLUs = state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs
            ?.filter((plu) => !swapIds.includes(plu))
            ?.concat(action.ingredient.IngredientPLU);

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple?.IngredientsChanges,
                    IngredientsRemoved: updatedIngredientsRemoved,
                    IngredientsSwapped: updatedIngredientsSwapped,
                    _SelectedIngredientPLUs: updatedSelectedIngredientPLUs,
                },
            },
        };
    }),
    on(actions.WizzardRemoveIngredient, (state, action) => {
        const ingredientToRemove = {
            IngredientPLU: action.ingredientPLU,
            IngredientName: action.ingredientName,
        };

        const updatedIngredientsRemoved = [...state.itemsSimple.IngredientsChanges.IngredientsRemoved, ingredientToRemove];

        const updatedSelectedIngredientPLUs = state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs?.filter((plu) => plu !== action.ingredientPLU);

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple?.IngredientsChanges,
                    IngredientsRemoved: updatedIngredientsRemoved,
                    _SelectedIngredientPLUs: updatedSelectedIngredientPLUs,
                },
            },
        };
    }),
    on(actions.WizzardSelectModifierForIngredient, (state, action) => {
        const isModifierAlreadyAdded = state.itemsSimple.IngredientsChanges.IngredientsModified.some(
            (ing) => ing.IngredientPLU === action.modifier.IngredientPLU && ing.ModifierID === action.modifier.ModifierID,
        );

        const modifierToAdd: OLO.State.Wizzard.IngredientModifiedModelExtended = {
            ExtraPrice: action.modifier.ExtraPrice,
            ModifierID: action.modifier.ModifierID,
            ModifierName: action.modifier.ModifierName,
            IngredientPLU: action.modifier.IngredientPLU,
            IngredientName: action.modifier.IngredientName,
        };

        const filteredIngredientsModified = state.itemsSimple.IngredientsChanges.IngredientsModified.filter((ing) => ing.IngredientPLU !== action.modifier.IngredientPLU);

        const updatedIngredientsModified = isModifierAlreadyAdded ? filteredIngredientsModified : filteredIngredientsModified.concat(modifierToAdd);

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple?.IngredientsChanges,
                    IngredientsModified: updatedIngredientsModified,
                },
            },
        };
    }),
    on(actions.WizzardAddSwap, (state, action) => {
        const newSwap = {
            ID: action.swap.NewProductID,
            OrderItemId: action.ingredient.IngredientProductID,
            OldIngredientPLU: action.ingredient.IngredientPLU,
            NewIngredientPLU: action.swap.NewPLU,
            OldIngredientName: action.ingredient.IngredientName,
            NewIngredientName: action.swap.Name,
            ExtraPrice: action.swap.ExtraPrice,
            _IngredientDisplayGroupId: action.ingredient.IngredientDisplayGroupId,
        };

        const updatedIngredientsSwapped = [...state.itemsSimple.IngredientsChanges.IngredientsSwapped, newSwap];

        const updatedIngredientsModified = state.itemsSimple.IngredientsChanges.IngredientsModified.filter((ing) => ing.IngredientPLU !== action.ingredient.IngredientPLU);

        const updatedIngredientsRemoved = state.itemsSimple.IngredientsChanges.IngredientsRemoved.filter((ing) => ing.IngredientPLU !== action.ingredient.IngredientPLU);

        const updatedSelectedIngredientPLUs = state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs
            ?.filter((plu) => plu !== action.ingredient.IngredientPLU)
            ?.concat(action.swap.NewPLU);

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple.IngredientsChanges,
                    IngredientsSwapped: updatedIngredientsSwapped,
                    IngredientsModified: updatedIngredientsModified,
                    IngredientsRemoved: updatedIngredientsRemoved,
                    _SelectedIngredientPLUs: updatedSelectedIngredientPLUs,
                },
            },
        };
    }),
    on(actions.WizzardRemoveSwap, (state, action) => {
        const updatedIngredientsSwapped = state.itemsSimple.IngredientsChanges.IngredientsSwapped.filter((ingredient) => ingredient.NewIngredientPLU !== action.newPLU);

        const updatedIngredientsRemoved = [
            ...state.itemsSimple.IngredientsChanges.IngredientsRemoved,
            {
                IngredientPLU: action.oldIngredientPLU,
                IngredientName: action.oldIngredientName,
            },
        ];

        const updatedSelectedIngredientPLUs = state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs?.filter((plu) => plu !== action.newPLU);

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple.IngredientsChanges,
                    IngredientsSwapped: updatedIngredientsSwapped,
                    IngredientsRemoved: updatedIngredientsRemoved,
                    _SelectedIngredientPLUs: updatedSelectedIngredientPLUs,
                },
            },
        };
    }),
    on(actions.WizzardSelectModifierForSwap, (state, action) => {
        const updatedIngredientsSwapped = state.itemsSimple.IngredientsChanges.IngredientsSwapped.map((ingredient) => {
            const isMatchingIngredient = action.modifier.IngredientPLU === ingredient.OldIngredientPLU;

            if (isMatchingIngredient) {
                const isSameModifier = ingredient.ModifierID === action.modifier.ModifierID;

                if (isSameModifier) {
                    return {
                        ...ingredient,
                        ExtraPrice: action.originalExtraPrice,
                        ModifierID: undefined,
                        ModifierName: undefined,
                    };
                }

                return {
                    ...ingredient,
                    ExtraPrice: action.modifier.ExtraPrice,
                    ModifierID: action.modifier.ModifierID,
                    ModifierName: action.modifier.ModifierName,
                };
            }

            return ingredient;
        });

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple.IngredientsChanges,
                    IngredientsSwapped: updatedIngredientsSwapped,
                },
            },
        };
    }),
    on(actions.WizzardSimpleItemSelectIngredient, (state, action) => {
        const updatedSelectedIngredientPLUs = [...(state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs || []), action.ingredientPLU];

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple.IngredientsChanges,
                    _SelectedIngredientPLUs: updatedSelectedIngredientPLUs,
                },
            },
        };
    }),
    on(actions.WizzardSimpleItemDeselectIngredient, (state, action) => {
        const updatedSelectedIngredientPLUs = state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs
            ? state.itemsSimple.IngredientsChanges._SelectedIngredientPLUs.filter((plu) => plu !== action.ingredientPLU)
            : [];

        return {
            ...state,
            itemsSimple: {
                ...state.itemsSimple,
                IngredientsChanges: {
                    ...state.itemsSimple.IngredientsChanges,
                    _SelectedIngredientPLUs: updatedSelectedIngredientPLUs,
                },
            },
        };
    }),
    on(actions.WizzardSimpleItemSpecialInstructions, (state, action) => ({
        ...state,
        itemsSimple: {
            ...state.itemsSimple,
            SpecialInstructions: action.specialInstruction,
        },
    })),
    on(actions.WizzardMenuFlowReplaceAllWithNew, (state, action) => ({
        ...state,
        recalculatePrices: {
            isCalculating: false,
            hasSucceeded: false,
            hasFailed: false,
        },
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Pages: state.itemsMenuFlow?.Pages?.map((Page) => {
                if (Page.PageIdentifier === action.pageIdentifier) {
                    return {
                        ...Page,
                        Products: [
                            {
                                ...action.product,
                                Quantity: action.product.Quantity ? action.product.Quantity : 1,
                            },
                        ],
                    };
                }

                return Page;
            }),
        },
    })),
    on(actions.WizzardRecalculatePricesForMenuFlowRequest, (state) => ({
        ...state,
        recalculatePrices: {
            isCalculating: true,
            hasSucceeded: false,
            hasFailed: false,
        },
    })),
    on(actions.WizzardRecalculatePricesForMenuFlowSuccessRequest, (state, action) => {
        const priceObj = action.priceObj;

        //
        //  Because pickupTimeValidator removes unsets everything on some actions taken, we need to
        //  check if state is still set or it's null.
        //
        return {
            ...state,
            recalculatePrices: {
                isCalculating: false,
                hasSucceeded: true,
                hasFailed: false,
            },
            itemsMenuFlow:
                state.itemsMenuFlow === null
                    ? null
                    : {
                          ...state.itemsMenuFlow,
                          UnitPrice: priceObj.UnitPrice,
                          UnitTotalValue: priceObj.UnitTotalValue,
                          Pages: state.itemsMenuFlow?.Pages?.map((Page) => {
                              const pageFromPriceObj = priceObj.Pages.find((obj) => obj.PageIdentifier === Page.PageIdentifier);

                              if (pageFromPriceObj) {
                                  return {
                                      ...Page,
                                      Products:
                                          Page?.Products?.map((Product) => {
                                              const productFromPriceObj = pageFromPriceObj.Products.find(
                                                  (obj) => obj.ProductId === Product.ProductId && obj.PageProductIdentifier === Product.PageProductIdentifier,
                                              );

                                              if (productFromPriceObj) {
                                                  return {
                                                      ...Product,
                                                      UnitPrice: productFromPriceObj.UnitPrice,
                                                      TotalValue: productFromPriceObj.TotalValue,
                                                  };
                                              }

                                              return Product;
                                          }) || [],
                                  };
                              }

                              return Page;
                          }),
                      },
        };
    }),
    on(actions.WizzardRecalculatePricesForMenuFlowErrorRequest, (state) => ({
        ...state,
        recalculatePrices: {
            isCalculating: false,
            hasSucceeded: false,
            hasFailed: true,
        },
    })),
    on(actions.WizzardMenuFlowAddProduct, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Pages: state.itemsMenuFlow?.Pages?.map((page) => {
                if (page.PageIdentifier === action.pageIdentifier) {
                    return {
                        ...page,
                        Products: [
                            ...(page.Products || []),
                            {
                                ...action.product,
                                Quantity: action.product.Quantity ? action.product.Quantity : 1,
                            },
                        ],
                    };
                }

                return page;
            }),
        },
    })),
    on(actions.WizzardMenuFlowRemoveProduct, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Pages:
                state.itemsMenuFlow?.Pages?.map((page) => {
                    if (page.PageIdentifier === action.pageIdentifier) {
                        return {
                            ...page,
                            Products: page?.Products?.filter((product) => product.ProductId !== action.productId) || [],
                        };
                    }

                    return page;
                }) || [],
        },
    })),
    on(actions.WizzardMenuFlowProductDecrement, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Pages:
                state.itemsMenuFlow?.Pages?.map((page) => {
                    if (page.PageIdentifier === action.pageIdentifier) {
                        return {
                            ...page,
                            Products:
                                page?.Products?.map((product) => {
                                    if (product.ProductId === action.productId) {
                                        return {
                                            ...product,
                                            Quantity: (product?.Quantity || 0) - action.changeValue,
                                        };
                                    }

                                    return product;
                                }) || [],
                        };
                    }

                    return page;
                }) || [],
        },
    })),
    on(actions.WizzardMenuFlowProductIncrement, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Pages:
                state.itemsMenuFlow?.Pages?.map((page) => {
                    if (page.PageIdentifier === action.pageIdentifier) {
                        return {
                            ...page,
                            Products:
                                page.Products?.map((product) => {
                                    if (product.ProductId === action.productId) {
                                        return {
                                            ...product,
                                            Quantity: (product?.Quantity || 0) + action.changeValue,
                                        };
                                    }

                                    return product;
                                }) || [],
                        };
                    }

                    return page;
                }) || [],
        },
    })),
    on(actions.WizzardMenuFlowProductChangeQuantity, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Pages:
                state.itemsMenuFlow?.Pages?.map((page) => {
                    if (page.PageIdentifier === action.pageIdentifier) {
                        return {
                            ...page,
                            Products:
                                page.Products?.map((product) => {
                                    if (product.ProductId === action.productId) {
                                        return {
                                            ...product,
                                            Quantity: action.changeValue,
                                        };
                                    }

                                    return product;
                                }) || [],
                        };
                    }

                    return page;
                }) || [],
        },
    })),
    on(actions.WizzardMenuFlowSelectModifier, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Pages:
                state.itemsMenuFlow?.Pages?.map((Page) => {
                    if (Page.PageIdentifier === action.pageIdentifier) {
                        return {
                            ...Page,
                            Products:
                                Page?.Products?.map((Product) => {
                                    if (Product.ProductId === action.productId) {
                                        const modifier = action.modifier;

                                        // Optional items put into IngredientsAdded array, mandatory IngredientsModified
                                        // for this project we only allow one modifier, but we keep it in array (same as online order)
                                        // remember to put this modifier into NEW array

                                        if (modifier._IsOptional) {
                                            const ingrExists = Product?.IngredientsChanges?.IngredientsAdded?.find((mod) => mod.ModifierID === modifier.ModifierID);

                                            if (ingrExists) {
                                                return Product;
                                            }

                                            return {
                                                ...Product,
                                                IngredientsChanges: {
                                                    ...Product.IngredientsChanges,
                                                    IngredientsAdded: [{ ...modifier }],
                                                },
                                            };
                                        }

                                        const ingredientExists = Product?.IngredientsChanges?.IngredientsModified?.find((mod) => mod.ModifierID === modifier.ModifierID);

                                        if (ingredientExists) {
                                            return Product;
                                        }

                                        return {
                                            ...Product,
                                            IngredientsChanges: {
                                                ...Product.IngredientsChanges,
                                                IngredientsModified: [{ ...modifier }],
                                            },
                                        };
                                    }

                                    return Product;
                                }) || [],
                        };
                    }

                    return Page;
                }) || [],
        },
    })),
    on(actions.WizzardMenuFlowDecrement, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Quantity: (state.itemsMenuFlow?.Quantity || 0) - action.changeValue,
        },
    })),
    on(actions.WizzardMenuFlowIncrement, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Quantity: (state?.itemsMenuFlow?.Quantity || 0) + action.changeValue,
        },
    })),
    on(actions.WizzardMenuFlowChangeQuantity, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            Quantity: action.changeValue,
        },
    })),
    on(actions.WizzardMenuFlowSpecialInstructions, (state, action) => ({
        ...state,
        itemsMenuFlow: {
            ...state.itemsMenuFlow!,
            SpecialInstructions: action.specialInstructions,
        },
    })),
);

export function wizzardReducer(state: OLO.State.Wizzard | undefined, action: Action) {
    return wizzardReducerFn(state, action);
}
